import {
  FlexContainer,
  LoadingPage,
} from '@act/storybook';
import type {
  ReportCustomerDto,
  UsStateCode,
  UpdateReportCustomerSettingsDto,
} from '@act/shared/data-transfer-objects';
import { US_STATE_OPTIONS } from '@act/shared/data-transfer-objects';
import {
  Chip,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { reportsRequests } from 'redux/requests/reports.requests';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { useToast } from 'react-components/app/ReusableToast';
import ReportsPageHeader from '../components/ReportsPageHeader';
import ReportsSurface from '../components/ReportsSurface';
import { useReportsPermissions } from '../hooks/useReportsPermissions.hook';
import ReportsUnauthorized from '../components/ReportsUnauthorized';
import { formatReportTimestamp } from '../utils/reportDisplay.util';

/**
 * Warehouse customer list with US state settings for county maps.
 */
const CustomersPage = () => {
  const toast = useToast();
  const { canManageCustomers } = useReportsPermissions();

  const [customers, loading, loadCustomers] = useApiCall(
    reportsRequests.listCustomers,
  );
  const [updatedCustomer, updating, updateCustomerSettings] = useApiCall(
    reportsRequests.updateCustomerSettings,
  );
  const [pendingCustomerName, setPendingCustomerName] = useState<string | null>(
    null,
  );

  useEffect(() => {
    loadCustomers({ params: { syncFromWarehouse: 'true' } });
  }, [loadCustomers]);

  useEffect(() => {
    if (updatedCustomer && updating === false && pendingCustomerName) {
      toast(`Updated state for ${pendingCustomerName}`, 'success');
      setPendingCustomerName(null);
      loadCustomers({ params: null });
    }
  }, [
    updatedCustomer,
    updating,
    pendingCustomerName,
    loadCustomers,
    toast,
  ]);

  if (!canManageCustomers) {
    return <ReportsUnauthorized />;
  }

  if (loading) {
    return <LoadingPage />;
  }

  const handleStateChange = (
    customer: ReportCustomerDto,
    usStateCode: UsStateCode | '',
  ) => {
    const body: UpdateReportCustomerSettingsDto = {
      usStateCode: usStateCode === '' ? null : usStateCode,
    };
    setPendingCustomerName(customer.name);
    updateCustomerSettings({ params: { id: customer.id }, body });
  };

  return (
    <FlexContainer
      direction="column"
      gap={2}
      width="100%"
      height="auto"
      overflowAllVisible={true}
    >
      <ReportsPageHeader
        title="Customer settings"
        subtitle="Customers are synced from the warehouse. Set the US state used for Who We're Reaching county maps."
      />

      <ReportsSurface padding={0}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell>US state</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(customers ?? []).map(customer => (
              <TableRow key={customer.id}>
                <TableCell>{customer.name}</TableCell>
                <TableCell>
                  <Select
                    size="small"
                    fullWidth
                    displayEmpty
                    disabled={updating === true}
                    value={customer.usStateCode ?? ''}
                    onChange={event =>
                      handleStateChange(
                        customer,
                        event.target.value as UsStateCode | '',
                      )
                    }
                  >
                    <MenuItem value="">
                      <Typography variant="body2" color="text.secondary">
                        Not configured
                      </Typography>
                    </MenuItem>
                    {US_STATE_OPTIONS.map(state => (
                      <MenuItem key={state.code} value={state.code}>
                        {state.label} ({state.code})
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  {customer.usStateCode ? (
                    <Chip label="Configured" size="small" color="success" />
                  ) : (
                    <Chip label="Needs state" size="small" color="warning" />
                  )}
                </TableCell>
                <TableCell>{formatReportTimestamp(customer.updatedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ReportsSurface>
    </FlexContainer>
  );
};

export default CustomersPage;
